import React from 'react';
import { CheckCircle2, Smartphone, FileCheck2 } from 'lucide-react';
import useIsDesktop from '../../hooks/useIsDesktop';
import Reveal from './Reveal';

function Hero() {
  const isDesktop = useIsDesktop();

  function scrollToQuickPay(e) {
    e.preventDefault();
    document.getElementById('quick-pay')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  return (
    <section style={styles.section}>
      <div style={{ ...styles.grid, flexDirection: isDesktop ? 'row' : 'column' }}>
        <Reveal style={styles.copy}>
          <p style={styles.eyebrow}>Ace Apartments · Eldoret</p>
          <h1 style={styles.headline}>Rent, sorted.</h1>
          <p style={styles.sub}>
            Pay rent, track every payment, and manage your property — tenants,
            landlords and admins, all in one place.
          </p>
          <div style={styles.ctaRow}>
            <a href="#quick-pay" onClick={scrollToQuickPay} style={styles.primaryCta} className="btn-lift">
              Pay Rent with M-Pesa
            </a>
            <a href="/register" style={styles.secondaryCta} className="btn-lift">
              Create an account
            </a>
          </div>
        </Reveal>

        <Reveal style={styles.visualWrap}>
          <div style={styles.blobA} />
          <div style={styles.blobB} />
          <div style={styles.mockCard}>
            <div style={styles.mockTopBar}>
              <span style={{ ...styles.mockDot, backgroundColor: '#e86a5c' }} />
              <span style={{ ...styles.mockDot, backgroundColor: '#e8c05c' }} />
              <span style={{ ...styles.mockDot, backgroundColor: '#5ce89a' }} />
            </div>
            <div style={styles.mockBody}>
              <p style={styles.mockUnit}>Rent Payment</p>

              <div style={styles.mockSuccessRow}>
                <div style={styles.mockCheckCircle}>
                  <CheckCircle2 size={26} />
                </div>
                <div>
                  <p style={styles.mockSuccessTitle}>Payment Successful</p>
                  <p style={styles.mockSuccessSub}>Confirmed instantly via M-Pesa</p>
                </div>
              </div>

              <div style={styles.mockRow}>
                <span style={styles.mockRowLabel}><Smartphone size={14} /> Method</span>
                <span style={styles.mockRowValue}>M-Pesa STK Push</span>
              </div>
              <div style={styles.mockRow}>
                <span style={styles.mockRowLabel}><FileCheck2 size={14} /> Receipt</span>
                <span style={styles.mockStatusBadge}>Sent instantly</span>
              </div>

              <div style={styles.mockPayBtn}>Pay Your Rent →</div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

const styles = {
  section: {
    padding: '64px 24px 32px',
    backgroundColor: 'var(--color-bg)',
  },
  grid: {
    maxWidth: '1200px',
    margin: '0 auto',
    display: 'flex',
    alignItems: 'center',
    gap: '56px',
  },
  copy: {
    flex: 1,
    minWidth: 0,
  },
  eyebrow: {
    fontSize: '13px',
    fontWeight: 700,
    color: 'var(--color-primary)',
    textTransform: 'uppercase',
    letterSpacing: '0.6px',
    margin: '0 0 14px',
  },
  headline: {
    fontFamily: 'var(--font-display)',
    fontSize: 'clamp(36px, 6vw, 58px)',
    fontWeight: 800,
    lineHeight: 1.05,
    letterSpacing: '-0.02em',
    color: 'var(--color-ink)',
    margin: '0 0 20px',
  },
  sub: {
    fontSize: '16px',
    lineHeight: 1.6,
    color: 'var(--color-ink-soft)',
    maxWidth: '460px',
    margin: '0 0 32px',
  },
  ctaRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
  },
  primaryCta: {
    padding: '14px 24px',
    borderRadius: '12px',
    backgroundColor: 'var(--color-primary)',
    color: 'var(--color-text-on-brand)',
    fontSize: '15px',
    fontWeight: 700,
    textDecoration: 'none',
  },
  secondaryCta: {
    padding: '14px 24px',
    borderRadius: '12px',
    backgroundColor: 'transparent',
    border: '1.5px solid var(--color-border)',
    color: 'var(--color-ink)',
    fontSize: '15px',
    fontWeight: 700,
    textDecoration: 'none',
  },
  visualWrap: {
    position: 'relative',
    flex: 1,
    minWidth: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '340px',
  },
  blobA: {
    position: 'absolute',
    width: '280px',
    height: '280px',
    borderRadius: '50%',
    background: 'var(--color-primary-soft)',
    opacity: 0.5,
    top: '-30px',
    right: '10%',
    filter: 'blur(6px)',
  },
  blobB: {
    position: 'absolute',
    width: '200px',
    height: '200px',
    borderRadius: '50%',
    background: 'var(--color-primary-light)',
    opacity: 0.6,
    bottom: '-20px',
    left: '8%',
  },
  mockCard: {
    position: 'relative',
    width: '320px',
    maxWidth: '100%',
    backgroundColor: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-lg)',
    boxShadow: 'var(--shadow-lg)',
    overflow: 'hidden',
  },
  mockTopBar: {
    display: 'flex',
    gap: '6px',
    padding: '12px 16px',
    borderBottom: '1px solid var(--color-border)',
  },
  mockDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
  },
  mockBody: {
    padding: '20px 22px 24px',
  },
  mockUnit: {
    fontSize: '12px',
    fontWeight: 700,
    color: 'var(--color-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    margin: '0 0 18px',
  },
  mockSuccessRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    marginBottom: '18px',
  },
  mockCheckCircle: {
    width: '44px',
    height: '44px',
    flexShrink: 0,
    borderRadius: '50%',
    backgroundColor: 'var(--color-primary-light)',
    color: 'var(--color-primary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  mockSuccessTitle: {
    fontFamily: 'var(--font-display)',
    fontSize: '17px',
    fontWeight: 800,
    color: 'var(--color-ink)',
    margin: '0 0 2px',
  },
  mockSuccessSub: {
    fontSize: '12.5px',
    color: 'var(--color-ink-soft)',
    margin: 0,
  },
  mockRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 0',
    borderTop: '1px solid var(--color-border-soft)',
  },
  mockRowLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    color: 'var(--color-ink-soft)',
  },
  mockRowValue: {
    fontSize: '13px',
    fontWeight: 700,
    color: 'var(--color-ink)',
  },
  mockStatusBadge: {
    fontSize: '12px',
    fontWeight: 700,
    color: 'var(--color-primary)',
    backgroundColor: 'var(--color-primary-light)',
    padding: '3px 10px',
    borderRadius: '999px',
  },
  mockPayBtn: {
    marginTop: '18px',
    padding: '12px',
    borderRadius: '10px',
    backgroundColor: 'var(--color-primary)',
    color: 'var(--color-text-on-brand)',
    fontSize: '14px',
    fontWeight: 700,
    textAlign: 'center',
  },
};

export default Hero;
